import { ref, computed } from "vue"

// No `@tauri-apps/plugin-os` dependency in this project — the WebView's own user agent already
// tells us the OS reliably (Tauri's Android WebView UA always includes "Android", and iOS's
// WKWebView UA always includes "iPhone"/"iPad"/"iPod"), so there's no need to add a plugin
// (+ its Rust crate, platform permissions, capability entries) just for this.
const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : ""
const isAndroid = /Android/i.test(userAgent)
const isIOS = /iPhone|iPad|iPod/i.test(userAgent)
const detectedIsMobile = isAndroid || isIOS

// Dev-only override so the mobile layout (bottom nav, hidden desktop-only panels) can be
// previewed from a normal desktop browser without an Android/iOS emulator. Module-level ref
// (same singleton pattern as `useChatStyle`/`useGroupDialog`) so every `usePlatform()` call site
// — HomepageLayout, dashboard.vue, group/[id].vue, etc. — agrees on the same value; hand-editing
// `isMobile` in just one of those files desyncs them and crashes pages that still assume desktop
// (e.g. `NavGroups`/`NavUser` calling `useSidebar()` outside the desktop `SidebarProvider`).
// Set it by visiting the app with `?mobile=1` (or `?mobile=0` to force desktop) once — the choice
// is persisted in localStorage across reloads. `?mobile=` with no value clears the override.
const STORAGE_KEY = "iopta:force-mobile-layout"

function readStoredOverride(): boolean | null {
  if (typeof localStorage === "undefined") return null
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === "true") return true
  if (raw === "false") return false
  return null
}

if (typeof window !== "undefined") {
  const param = new URLSearchParams(window.location.search).get("mobile")
  if (param === "1" || param === "true") localStorage.setItem(STORAGE_KEY, "true")
  else if (param === "0" || param === "false") localStorage.setItem(STORAGE_KEY, "false")
  else if (param === "") localStorage.removeItem(STORAGE_KEY)
}

const mobileOverride = ref<boolean | null>(readStoredOverride())
const isMobile = computed(() => mobileOverride.value ?? detectedIsMobile)

function setMobileOverride(value: boolean | null) {
  mobileOverride.value = value
  if (typeof localStorage === "undefined") return
  if (value === null) localStorage.removeItem(STORAGE_KEY)
  else localStorage.setItem(STORAGE_KEY, String(value))
}

export function usePlatform() {
  return {
    isAndroid,
    isIOS,
    isMobile,
    setMobileOverride,
  }
}
